import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

const typeFilters: { value: 'all' | 'movie' | 'tv' | 'variety'; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'movie', label: '电影' },
  { value: 'tv', label: '电视剧' },
  { value: 'variety', label: '综艺' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(initialKeyword);
  const [activeType, setActiveType] = useState<'all' | 'movie' | 'tv' | 'variety'>('all');

  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  const results = useMemo(() => {
    const q = initialKeyword.trim().toLowerCase();
    if (!q) return [];
    return movies.filter((movie) => {
      const matchKeyword =
        movie.title.toLowerCase().includes(q) ||
        movie.tags.some((t) => t.toLowerCase().includes(q)) ||
        movie.genres.some((g) => g.toLowerCase().includes(q)) ||
        movie.actors.some((a) => a.toLowerCase().includes(q));
      const matchType = activeType === 'all' || movie.type === activeType;
      return matchKeyword && matchType;
    });
  }, [initialKeyword, activeType]);

  const suggestions = useMemo(() => [...movies].sort((a, b) => b.rating - a.rating).slice(0, 4), []);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Seo
        title={initialKeyword ? `“${initialKeyword}”的搜索结果 - 菠萝蜜网` : '全站搜索 - 菠萝蜜网'}
        description={`在菠萝蜜网搜索${initialKeyword || '电影、电视剧、综艺'}相关影视资源，快速找到你感兴趣的影视作品详情与资料。`}
        keywords={`菠萝蜜网,搜索,${initialKeyword || '影视搜索'}`}
      />
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">全站搜索</h1>

          <form onSubmit={handleSearch} className="relative max-w-xl">
            <input
              type="text"
              name="keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、综艺名称..."
              className="w-full h-12 pl-12 pr-28 rounded-full bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <button
              type="submit"
              className="absolute right-1.5 top-1.5 bottom-1.5 px-5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          {initialKeyword && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <p className="text-sm text-foreground-600">
                关键词 <span className="text-primary-600 font-medium">“{initialKeyword}”</span> 共找到 {results.length} 个结果
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 ml-auto">
                {typeFilters.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setActiveType(item.value)}
                    className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                      activeType === item.value ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            {!initialKeyword ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <i className="ri-search-eye-line text-5xl text-background-300 mb-4"></i>
                <p className="text-foreground-600">请输入关键词，开始搜索您感兴趣的影视作品</p>
              </div>
            ) : results.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showType />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <i className="ri-emotion-sad-line text-5xl text-background-300 mb-4"></i>
                <p className="text-foreground-600">抱歉，没有找到与“{initialKeyword}”相关的影视作品</p>
                <p className="mt-1 text-xs text-foreground-400">试试更换关键词，或看看下方的热门推荐</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5 mt-8 w-full max-w-3xl">
                  {suggestions.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} showType />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 text-center">
            <Link to="/" className="text-sm text-primary-600 hover:text-accent-600 cursor-pointer">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}